import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Snackbar,
  Alert,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";

const UserFormDialog = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  mode,
  availableRoles = [],
  showAssociatedAccount = true,
  hideRoleSelection = false,
}) => {
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmPassword("");
      setPasswordError("");
      setOpenSnackbar(false);
    }
  }, [open]);

  const roles = Array.isArray(formData.Roles) ? formData.Roles : [];

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const validatePasswords = () => {
    if ((formData.Password || "").length < 8) {
      setPasswordError("Password must be at least 8 characters long.");
      return false;
    }

    if (formData.Password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return false;
    }

    setPasswordError("");
    return true;
  };

  const handleSubmit = () => {
    if (mode === "add" && !validatePasswords()) return;

    const normalizedData = {
      ...formData,
      AssociatedAccounts: Array.isArray(formData.AssociatedAccounts)
        ? formData.AssociatedAccounts.map((acc) => acc.trim()).filter(Boolean)
        : [],
    };

    onSubmit(normalizedData);
    setOpenSnackbar(true);
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          {mode === "add" ? "Add New User" : "Edit User"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            {mode === "add"
              ? "Enter the user details below to create a new account."
              : "Update the user details below."}
          </Typography>

          <TextField
            label="Username"
            fullWidth
            value={formData.Username || ""}
            onChange={handleChange("Username")}
            sx={fieldSx}
          />

          {showAssociatedAccount && (
            <Box
              sx={{
                mt: 2,
                px: 1.25,
                py: 1.25,
                borderRadius: "12px",
                backgroundColor: "rgba(47,99,173,0.04)",
                border: "1px solid rgba(47,99,173,0.08)",
              }}
            >
              <Autocomplete
                multiple
                freeSolo
                filterSelectedOptions
                options={[]}
                value={formData.AssociatedAccounts || []}
                onChange={(e, newValue) =>
                  setFormData({
                    ...formData,
                    AssociatedAccounts: newValue
                      .map((acc) => acc.trim())
                      .filter(Boolean),
                  })
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "rgba(47,99,173,0.10)",
                        color: "#2f63ad",
                        fontWeight: 600,
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <span>Associated Accounts</span>
                        <Tooltip title="Press Enter after typing each account ID">
                          <InfoIcon sx={{ fontSize: 16, color: "gray" }} />
                        </Tooltip>
                      </Box>
                    }
                    fullWidth
                    helperText="Type an account ID and press Enter to add it"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255,255,255,0.96)",
                        borderRadius: "10px",
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#6b778c",
                        fontWeight: 500,
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}

          {mode === "add" && (
            <>
              <TextField
                label="Password"
                type="password"
                fullWidth
                required
                value={formData.Password || ""}
                onChange={handleChange("Password")}
                error={!!passwordError}
                sx={fieldSx}
              />

              <TextField
                label="Confirm Password"
                type="password"
                fullWidth
                required
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                error={!!passwordError}
                helperText={passwordError}
                sx={fieldSx}
              />
            </>
          )}

          <TextField
            label="First Name"
            fullWidth
            value={formData.Name || ""}
            onChange={handleChange("Name")}
            sx={fieldSx}
          />

          <TextField
            label="Last Name"
            fullWidth
            value={formData.Lastname || ""}
            onChange={handleChange("Lastname")}
            sx={fieldSx}
          />

          <TextField
            label="Email"
            fullWidth
            value={formData.Email || ""}
            onChange={handleChange("Email")}
            sx={fieldSx}
          />

          {!hideRoleSelection && (
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={roles}
                onChange={(e) =>
                  setFormData({ ...formData, Roles: e.target.value })
                }
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) =>
                  availableRoles
                    .filter((role) => selected.includes(role.RoleID))
                    .map((role) => role.RoleName)
                    .join(", ")
                }
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.RoleID} value={role.RoleID}>
                    <Checkbox checked={roles.includes(role.RoleID)} />
                    <ListItemText primary={role.RoleName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box
            sx={{
              mt: 2,
              px: 1.25,
              py: 1.1,
              borderRadius: "12px",
              backgroundColor: "rgba(47,99,173,0.04)",
              border: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.SendInvite || false}
                  onChange={(e) =>
                    setFormData({ ...formData, SendInvite: e.target.checked })
                  }
                />
              }
              label="Send invitation email"
              sx={{ m: 0 }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button onClick={onClose} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>

          <Button onClick={handleSubmit} variant="contained" sx={primaryButtonSx}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {mode === "add"
            ? "User added successfully!"
            : "User updated successfully!"}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserFormDialog;
